import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import AuthLayout from "../layouts/AuthLayout.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import apiClient from "../services/api.js";
import { defaultRouteForMember } from "../utils/routes.js";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: params.get("email") || "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const verificationComplete =
    params.get("verified") === "1" || location.state?.verificationComplete === true;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNeedsVerify(false);
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/login", form);
      login(data);
      navigate(defaultRouteForMember(data.member), { replace: true });
    } catch (err) {
      const message = err?.response?.data?.message || "Login failed";
      setError(message);
      if (err?.response?.data?.requires_verification) {
        setNeedsVerify(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={verificationComplete ? "Your account is ready" : "Welcome back"}
      subtitle={
        verificationComplete
          ? "Your email has been verified successfully. Sign in to continue to your member dashboard."
          : "Sign in to manage your cooperative savings, contributions, and finance requests."
      }
      steps={verificationComplete ? ["Account", "Verify", "Sign in"] : undefined}
      currentStep={verificationComplete ? 3 : undefined}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <span>New member?</span>
          <Link className="font-semibold text-accent" to="/register">
            Create an account
          </Link>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        {verificationComplete && (
          <p
            aria-live="polite"
            className="rounded-2xl border border-mint/30 bg-mint/10 px-4 py-3 text-sm font-medium text-emerald-800"
          >
            ✓ Email verified. You can now sign in securely.
          </p>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="input mt-2"
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              Password
            </label>
            <Link
              className="text-xs font-semibold text-accent"
              to={`/forgot-password${form.email ? `?email=${encodeURIComponent(form.email)}` : ""}`}
            >
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            className="input mt-2"
            placeholder="********"
            autoComplete="current-password"
            required
          />
        </div>
        {error && (
          <p role="alert" className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </p>
        )}
        {needsVerify && (
          <p className="text-xs text-slate-600">
            Verify your email first.{" "}
            <Link className="font-semibold text-accent" to={`/verify?email=${encodeURIComponent(form.email)}`}>
              Verify now
            </Link>
            .
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-2xl bg-accent px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-accent/90"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
